import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Typography, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import SectionCard from '../SectionCard';
import InvButton from '../InvButton';
import { InvSelect, InvTextField } from '../InvField';
import StockBalanceAutocomplete from '../StockBalanceAutocomplete';
import { splitStockBatch } from '../../api/stockAdjustment';
import { getStores } from '../../api/stockLedger';
import { toInventoryList } from '../../api/inventory-request';
import { storeDisplayName } from '../../utils/storeEntity';
import { IPD_COLORS } from '../../theme';

const SPLIT_DEFAULT = { batch_number: '', quantity: '', expiry_date: '' };

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Immediate batch split — POST /inventory/stock/batches/split/
 * Split quantities must sum exactly to source on-hand.
 */
export default function SplitBatchPanel() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [itemKey, setItemKey] = useState('');
  const [catalogueItem, setCatalogueItem] = useState(null);
  const [sourceBatch, setSourceBatch] = useState('');
  const [grnItemId, setGrnItemId] = useState('');
  const [sourceOnHand, setSourceOnHand] = useState(null);
  const [sourceExpiry, setSourceExpiry] = useState('');
  const [splits, setSplits] = useState([{ ...SPLIT_DEFAULT }, { ...SPLIT_DEFAULT }]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  const batchOptions = (catalogueItem?.batches || [])
    .filter(b => !b.is_expired && toNumber(b.on_hand_quantity) > 0)
    .map(b => ({
      value: b.batch_number || '',
      label: `${b.batch_number || '—'} · On hand ${b.on_hand_quantity ?? '—'}`,
      on_hand: b.on_hand_quantity,
      expiry_date: b.expiry_date || '',
      grn_item_id: b.grn_item_id || '',
    }))
    .filter(o => o.value);

  function onItemChange(key, item) {
    setItemKey(key || '');
    setCatalogueItem(item);
    setSourceBatch('');
    setGrnItemId('');
    setSourceOnHand(null);
    setSourceExpiry('');
    setResult(null);
  }

  function onSourceBatchChange(value) {
    setSourceBatch(value);
    const match = batchOptions.find(o => o.value === value);
    setSourceOnHand(match ? toNumber(match.on_hand) : null);
    setSourceExpiry(match?.expiry_date || '');
    setGrnItemId(match?.grn_item_id || '');
    setSplits([
      { batch_number: '', quantity: '', expiry_date: match?.expiry_date || '' },
      { batch_number: '', quantity: '', expiry_date: match?.expiry_date || '' },
    ]);
    setResult(null);
  }

  function updateSplit(i, key, val) {
    setSplits(prev => prev.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  }

  const splitTotal = useMemo(
    () => splits.reduce((sum, row) => sum + (toNumber(row.quantity) || 0), 0),
    [splits],
  );

  const remaining = sourceOnHand != null
    ? Number((sourceOnHand - splitTotal).toFixed(6))
    : null;

  const canSubmit = Boolean(
    storeId
    && catalogueItem?.item_id
    && sourceBatch
    && sourceOnHand != null
    && splits.length >= 2
    && splits.every(s => s.batch_number.trim() && toNumber(s.quantity) > 0)
    && Math.abs(remaining) < 0.000001
  );

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) {
      toast.error('Split quantities must sum exactly to source on-hand');
      return;
    }
    const batchNos = splits.map(s => s.batch_number.trim());
    if (new Set(batchNos).size !== batchNos.length) {
      toast.error('Split batch numbers must be unique');
      return;
    }
    if (batchNos.includes(sourceBatch)) {
      toast.error('New batch numbers must differ from the source batch');
      return;
    }

    setSaving(true);
    try {
      // API: POST /inventory/stock/batches/split/
      const data = await splitStockBatch({
        store_id: storeId,
        item_id: catalogueItem.item_id,
        source_batch_number: sourceBatch,
        grn_item_id: grnItemId || undefined,
        splits: splits.map(s => ({
          batch_number: s.batch_number.trim(),
          quantity: Number(s.quantity),
          expiry_date: s.expiry_date || undefined,
        })),
      });
      setResult(data);
      toast.success('Batch split completed');
    } catch (err) {
      toast.error(err.message || 'Failed to split batch');
    } finally {
      setSaving(false);
    }
  }

  const storeOptions = stores.map(s => ({
    value: s.store_id || s.id,
    label: storeDisplayName(s),
  }));

  return (
    <SectionCard
      title="Split Batch"
      subtitle="Redistribute one source batch into two or more new batches (net stock unchanged)."
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Split quantities must sum exactly to the source on-hand. Ledger records ADJUSTMENT_OUT / ADJUSTMENT_IN.
      </Alert>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <InvSelect
              label="Store *"
              value={storeId}
              onChange={e => {
                setStoreId(e.target.value);
                onItemChange('', null);
              }}
              options={storeOptions}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <StockBalanceAutocomplete
              storeId={storeId}
              value={itemKey}
              onChange={onItemChange}
              label="Item *"
              placeholder="Search item (min 2 chars)…"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InvSelect
              label="Source batch *"
              value={sourceBatch}
              onChange={e => onSourceBatchChange(e.target.value)}
              options={batchOptions}
              disabled={!catalogueItem}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ px: 1.5, py: 1.25, border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block' }}>
                Source on-hand
              </Typography>
              <Typography sx={{ fontWeight: 600, color: IPD_COLORS.textPrimary }}>
                {sourceOnHand != null ? sourceOnHand : '—'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ px: 1.5, py: 1.25, border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block' }}>
                Remaining to allocate
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: remaining === 0
                    ? IPD_COLORS.success.text
                    : (remaining != null && remaining < 0 ? IPD_COLORS.error.text : IPD_COLORS.textPrimary),
                }}
              >
                {remaining != null ? remaining : '—'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontWeight: 600, color: IPD_COLORS.textPrimary }}>New batches (≥ 2)</Typography>
            <InvButton
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setSplits(prev => [...prev, { batch_number: '', quantity: '', expiry_date: sourceExpiry }])}
            >
              Add split
            </InvButton>
          </Box>
          <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch *</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 140 }}>Quantity *</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 160 }}>Expiry</TableCell>
                <TableCell sx={{ width: 50 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {splits.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <InvTextField
                      value={row.batch_number}
                      label=""
                      onChange={e => updateSplit(i, 'batch_number', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <InvTextField
                      value={row.quantity}
                      label=""
                      type="number"
                      inputProps={{ min: 0.01, step: 0.01 }}
                      onChange={e => updateSplit(i, 'quantity', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <InvTextField
                      value={row.expiry_date}
                      label=""
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      onChange={e => updateSplit(i, 'expiry_date', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      disabled={splits.length <= 2}
                      onClick={() => setSplits(prev => prev.filter((_, idx) => idx !== i))}
                      sx={{ color: IPD_COLORS.error.text }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <InvButton type="submit" loading={saving} disabled={!canSubmit}>
            Split batch
          </InvButton>
        </Box>
      </Box>

      {result && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Split complete
          {Array.isArray(result.new_batches) && result.new_batches.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {result.new_batches.map(b => (
                <li key={b.batch_number || b.stock_balance_id}>
                  {b.batch_number} — qty {b.quantity}
                  {b.expiry_date ? ` · exp ${b.expiry_date}` : ''}
                </li>
              ))}
            </Box>
          )}
        </Alert>
      )}
    </SectionCard>
  );
}
